import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FulfillService } from '../../fulfill.service';
import { PanelState, OrderInBulkFulfillmentProcess } from '../../utils/models';
import moment from 'moment';

import {
  PrintDocumentType,
  buildPrintOptions,
  PrintBatchOptionFunctionsMap,
  DocumentTypeDisplayName,
  PrintOptionsDisplayName,
  extractPrintSectionsAndOptions,
} from 'src/utils/print-utils';
import { PrintService } from '@app/services/print/print.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { FreightServiceType } from '@pdqdev/pdq-shared/dist/CarrierData';
import { OrderBatch } from '@services/order-batch/order-batch.model';
import { ApiService } from '@app/core/services/api/api.service';

const PrintActions = [
  PrintDocumentType.LABELS,
  PrintDocumentType.PICK_LIST,
  PrintDocumentType.PACKING_SLIPS,
  PrintDocumentType.PACK_AND_SHIP,
  PrintDocumentType.BROCHURE,
  PrintDocumentType.COMMERCIAL_INVOICE,
  PrintDocumentType.GREETING_CARD,
  PrintDocumentType.CARD_ISLE,
];

@Component({
  selector: 'fulfill-batch-list-table-row',
  templateUrl: './fulfill-batch-list-table-row.component.html',
  styleUrls: ['./fulfill-batch-list-table-row.component.scss'],
  standalone: false,
})
export class FulfillBatchListTableRowComponent implements OnInit, OnDestroy {
  @Input() batch: OrderBatch;
  @Input() state;
  @Input() variant: 'secondary' | undefined;
  printOptions: any[] = [];
  printSections: any[] = [];
  isSecondary = false;
  isTPL: boolean = false;
  canBeMovedBackToASsign;
  printPlaceholder;
  labelUrl;
  packAndShipUrl;
  packingSlipsUrl;
  commercialInvoiceUrl;
  hasAddedOrders = false;
  destroy$ = new Subject();

  constructor(
    private fulfillService: FulfillService,
    private changeDetectorRef: ChangeDetectorRef,
    private _printService: PrintService,
    private apiService: ApiService,
  ) {}

  ngOnInit(): void {
    this.isTpl();
    const carrierTypeCustomFields = this.batch.selectedCarrier?.type?.custom_fields;
    buildPrintOptions(
      this.fulfillService.shopMetadata,
      PrintActions,
      'batch',
      carrierTypeCustomFields,
      this.apiService,
      () => this.fulfillService.getCardIsleDevicesCached(),
    ).then((options) => {
      const formatPrintOptions = extractPrintSectionsAndOptions(options);
      this.printOptions = formatPrintOptions.printOptions;
      this.printSections = formatPrintOptions.printSections;
      this.changeDetectorRef.detectChanges();
    });

    if (this.variant == 'secondary') this.isSecondary = true;
    this.canBeMovedBackToASsign = this.canBeMovedBackToAssign();
    this.printPlaceholder = this.state === 'fulfillmentReady' ? 'Print' : 'Printed';
    this.initDocumentPreparationListener(this.fulfillService.onPackingSlipsUrls, PrintDocumentType.PACKING_SLIPS);
    this.initDocumentPreparationListener(this.fulfillService.onPackAndShipUrls, PrintDocumentType.PACK_AND_SHIP);
    this.initPrintLabelsUrlSubject();
    this.hasAddedOrders = this.batch.orders.some((order) => order.deliveryTransaction?.isAddedToBatch);
  }

  ngOnDestroy() {}

  setActiveBatch(event) {
    if (event.target.closest('.print')?.contains(event.target)) {
      event.stopPropagation();
    } else if (!this.isSecondary) {
      this.fulfillService.setActiveBatch(this.batch);
      this.fulfillService.setPanelState(PanelState.BATCH);
    }
  }

  initDocumentPreparationListener(subject$: Subject<any>, document: PrintDocumentType) {
    subject$.pipe(takeUntil(this.destroy$)).subscribe(({ batchId, url }) => {
      const documentOption = this.printOptions?.find((option) => option.value === document);

      switch (document) {
        case PrintDocumentType.PACKING_SLIPS:
          this.packingSlipsUrl = url;
          break;
        case PrintDocumentType.PACK_AND_SHIP:
          this.packAndShipUrl = url;
          break;
      }
      if (documentOption && this.batch.id == batchId) {
        documentOption.loading = false;
        documentOption.disabled = false;
        documentOption.label = PrintOptionsDisplayName[document];
        this.printOptions = [...this.printOptions];
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  initPrintLabelsUrlSubject() {
    this.fulfillService.onLabelsUrls
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ batchId, labelUrl, commercialInvoiceUrl }) => {
        this.labelUrl = labelUrl;
        this.commercialInvoiceUrl = commercialInvoiceUrl;

        const commercialInvoiceOptionIndex = this.printOptions.findIndex(
          (printOption) => printOption.value === PrintDocumentType.COMMERCIAL_INVOICE,
        );

        if (commercialInvoiceOptionIndex > -1 && commercialInvoiceUrl) {
          this.printOptions[commercialInvoiceOptionIndex].loading = false;
          this.printOptions[commercialInvoiceOptionIndex].hidden = false;
          this.printOptions[commercialInvoiceOptionIndex].label = 'Print commercial invoice';
        }
        // hide Print commercial invoice option:
        else if (commercialInvoiceOptionIndex > -1 && !commercialInvoiceUrl) {
          this.printOptions[commercialInvoiceOptionIndex].loading = false;
          this.printOptions[commercialInvoiceOptionIndex].hidden = true;
        }

        if (
          this.batch.id == batchId &&
          !this.fulfillService?.shopMetadata?.disablePrintLabels &&
          this.printOptions?.length > 0
        ) {
          this.printOptions[0].loading = false;
          this.printOptions[0].label = 'Print Labels';
          this.printOptions[0].disabled = false;
          this.printOptions = [...this.printOptions];
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  async print(event, batchId) {
    let url = this.labelUrl;
    if (event.action === 'openCommercialInvoiceWindow') {
      url = this.commercialInvoiceUrl;
    }
    switch (event.value) {
      case PrintDocumentType.PACKING_SLIPS:
        url = this.packingSlipsUrl;
        break;
      case PrintDocumentType.PACK_AND_SHIP:
        url = this.packAndShipUrl;
        break;
    }
    this.onBatchPrintTriggerSetBulkFulfillmentData(event.action);
    this.printOptions.forEach((option) => {
      option.disabled = false;
      option.loading = false;
    });
    this.printOptions = [...this.printOptions];
    this._printService.printBatch(event.action, { batchId, url, documentName: event.action });
  }

  onBatchPrintTriggerSetBulkFulfillmentData(action) {
    const metadata = this.fulfillService.shopMetadata;
    if (metadata?.onPrintTriggerFulfillment && action === PrintBatchOptionFunctionsMap['labels']) {
      this.fulfillService.setBulkBatchFulfillmentProcessData({
        batchId: this.batch.id,
        orders: this.batch.orders?.map((order) => ({
          id: order.id,
          fulfillmentProcess: 'running',
        })) as OrderInBulkFulfillmentProcess[],
      });
    }
  }

  async prepareLabels(event, batchId) {
    this.prepareDocument(PrintDocumentType.PACKING_SLIPS, batchId);
    this.prepareDocument(PrintDocumentType.PACK_AND_SHIP, batchId);
    if (!this.fulfillService?.shopMetadata?.disablePrintLabels && this.printOptions?.length > 0) {
      this.labelUrl = null;
      this.printOptions[0].loading = true;
      this.printOptions[0].label = 'Labels';
      this.printOptions[0].disabled = true;
      const commercialInvoiceOptionIndex = this.printOptions.findIndex(
        (printOption) => printOption.value === PrintDocumentType.COMMERCIAL_INVOICE,
      );
      if (commercialInvoiceOptionIndex > -1) {
        this.printOptions[commercialInvoiceOptionIndex].loading = true;
        this.printOptions[commercialInvoiceOptionIndex].label = '';
      }
      await this.fulfillService.prepareLabels(batchId);
    }
  }

  async prepareDocument(document: PrintDocumentType, batchId) {
    const documentOption = this.printOptions?.find((option) => option.value === document);
    if (documentOption) {
      documentOption.loading = true;
      documentOption.disabled = true;
      documentOption.label = DocumentTypeDisplayName[document];

      switch (document) {
        case PrintDocumentType.PACKING_SLIPS:
          this.packingSlipsUrl = null;
          this.fulfillService.preparePackingSlips(batchId);
          break;
        case PrintDocumentType.PACK_AND_SHIP:
          this.packAndShipUrl = null;
          this.fulfillService.preparePackAndShip(batchId);
          break;
      }
    }
  }

  displayBatchFailedText(failedNum) {
    switch (failedNum) {
      case 0:
        return 'No labels generated';
      case 1:
        return failedNum + " label generation failed. it's now back in the Assign screen";
      default:
        return failedNum + " label generations failed. They're back in the Assign screen";
    }
  }

  isTpl() {
    try {
      this.isTPL = this.batch.selectedCarrier.freightServiceType[0] === FreightServiceType.FULFILLMENT_SERVICE_3PL;
    } catch (e) {
      this.isTPL = false;
    }
  }

  canBeMovedBackToAssign() {
    const daysDiff = moment(this.batch.createdDate) < moment().add(-3, 'day');
    return !daysDiff;
  }
}
